import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "../shell/PublicLayout";
import Badge from "../components/Badge";
import Button from "../components/Button";
import EmptyState from "../components/EmptyState";
import { GetCurrentMonth } from "../../service/service";
import { paths } from "../routing/paths";

/*
 * /monthly-classes in the redesign — the Jewish-calendar shelf for the
 * current month (user/current-month): one category row per calendar date
 * or Yom Tov. Replaces the legacy MonthlyClasses (Banner + DataItem).
 * The API's category slugs are full nested paths, so rows link straight
 * to the permalink catch-all (the backend resolves them with or without
 * the legacy /category prefix). Navy hero + one white card of rows,
 * list-category grammar like TagPage.
 */

const calendarIcon = (size = 40) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="3" />
    <path d="M8 3v4M16 3v4M3 10h18" />
    <circle cx="12" cy="15.5" r="1" fill="#3B82F6" stroke="none" />
  </svg>
);

export default function MonthlyClassesPage() {
  // null = loading · {month, items} = loaded · items [] = nothing/failed
  const [shelf, setShelf] = useState(null);

  useEffect(() => {
    let cancelled = false;
    GetCurrentMonth()
      .then((result) => {
        if (cancelled) return;
        const data = result?.status ? result.data : null;
        setShelf({
          month: data?.month || "",
          items: (data?.category || []).filter((c) => c && c.slug),
        });
      })
      .catch(() => !cancelled && setShelf({ month: "", items: [] }));
    return () => {
      cancelled = true;
    };
  }, []);

  const countLine = shelf && shelf.items.length > 0
    ? `${shelf.items.length} ${shelf.items.length === 1 ? "category" : "categories"} this month`
    : null;

  return (
    <PublicLayout>
      <Helmet>
        <title>{shelf?.month ? `${shelf.month} classes | Inside Chassidus` : "Monthly classes | Inside Chassidus"}</title>
      </Helmet>
      <main className="max-w-[960px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">This month's classes</span>
        </nav>

        {/* Loading skeleton */}
        {shelf === null && (
          <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading this month's classes">
            <div className="h-44 rounded-xl bg-ic-lightGray animate-pulse" />
            <div className="h-96 rounded-xl bg-ic-lightGray animate-pulse" />
          </div>
        )}

        {shelf !== null && (
          <>
            {/* Month hero — navy, list-category grammar */}
            <section
              className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark flex gap-5 items-start flex-wrap"
              style={{ padding: "clamp(20px, 5vw, 32px)" }}
            >
              <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-ic-navyCard hidden xs:flex items-center justify-center">
                {calendarIcon()}
              </div>
              <div className="flex-1 min-w-[240px] flex flex-col gap-2">
                <span className="self-start">
                  <Badge variant="onNavy">JEWISH AND CHASSIDIC CALENDAR</Badge>
                </span>
                <h1 className="m-0 font-bold text-white tracking-[-0.4px] ic-wrap-anywhere" style={{ fontSize: "clamp(26px, 6vw, 36px)", lineHeight: 1.25 }}>
                  {shelf.month ? `Classes for ${shelf.month}` : "This month's classes"}
                </h1>
                <p className="m-0 text-[18px] font-normal leading-7 text-ic-offwhite max-w-[640px]">
                  The dates and Yomim Tovim of the Jewish month, each with its classes from the archive.
                </p>
                {countLine && <span className="text-[14px] leading-5 text-ic-lightGray">{countLine}</span>}
              </div>
            </section>

            {/* Category rows */}
            {shelf.items.length > 0 && (
              <section className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised overflow-hidden">
                {shelf.items.map((cat, i) => {
                  const name = cat.title || cat.name;
                  const description = cat.description && cat.description !== name ? cat.description : null;
                  return (
                    <Link
                      key={cat.id || cat.slug}
                      to={paths.permalink(cat.slug)}
                      className={`flex items-center gap-4 px-5 py-3.5 no-underline hover:no-underline transition-colors duration-150 ease-out hover:bg-ic-offwhite focus-visible:outline-none focus-visible:shadow-ic-focus-soft ${
                        i === shelf.items.length - 1 ? "" : "border-b border-ic-lightGray"
                      }`}
                    >
                      <span className="flex-shrink-0 w-11 h-11 border border-ic-borderGray rounded-2xl inline-flex items-center justify-center">
                        {calendarIcon(22)}
                      </span>
                      <span className="flex-1 min-w-0 flex flex-col gap-1">
                        <span className="text-[18px] leading-7 font-semibold text-black ic-wrap-anywhere">{name}</span>
                        {description && <span className="text-[14px] leading-5 text-ic-slate ic-wrap-anywhere">{description}</span>}
                      </span>
                      <span aria-hidden="true" className="flex-shrink-0 text-ic-slate">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 6l6 6-6 6" />
                        </svg>
                      </span>
                    </Link>
                  );
                })}
              </section>
            )}

            {/* Designed empty state — never blank */}
            {shelf.items.length === 0 && (
              <EmptyState
                icon={calendarIcon(28)}
                title="This month's shelf isn't loading"
                body="Every calendar date's classes live in the archive — browse by category instead."
                actions={
                  <Link to={paths.categories()}>
                    <Button size="lg">Browse all categories</Button>
                  </Link>
                }
              />
            )}
          </>
        )}
      </main>
    </PublicLayout>
  );
}
